// Caminho absoluto: src/components/HUD/HUD.tsx

import { useEffect, useRef, useState } from 'react';
import { Heart, Coins, Shield, ShoppingCart, Volume2, VolumeX, LogOut, AlertTriangle } from 'lucide-react';
import { useQuiz } from '../../context/QuizContext';
import { isActiveRunStatus } from '../../utils/quizUtils';
import { useGameAudio } from '../../hooks/useGameAudio';
import { AudioBus } from '../../utils/AudioBus';

export const HUD = () => {
  const { state, dispatch } = useQuiz();
  const { isMuted, toggleMute, playHover, playClick, playBack } = useGameAudio();
  const [showConfirmQuit, setShowConfirmQuit] = useState(false);

  // Game feel: o HUD reage ao recurso mudar — ganho de Axé faz a moeda pulsar,
  // perda de Búzio faz o coração estilhaçar. O `key` que incrementa reinicia a
  // animação CSS. Só lê o estado (nenhuma mecânica é tocada).
  const prevMoedas = useRef(state.moedas);
  const prevVidas = useRef(state.vidas);
  const [coinPulse, setCoinPulse] = useState(0);
  const [heartHit, setHeartHit] = useState(0);

  useEffect(() => {
    if (state.moedas > prevMoedas.current) setCoinPulse((p) => p + 1);
    prevMoedas.current = state.moedas;
  }, [state.moedas]);

  useEffect(() => {
    if (state.vidas < prevVidas.current) {
      setHeartHit((h) => h + 1);
      AudioBus.play('feedback.lifeLost'); // coração estilhaça = Búzio perdido
    }
    prevVidas.current = state.vidas;
  }, [state.vidas]);

  const faseCores: Record<number, string> = {
    1: 'text-amber-400 border-amber-500/30',
    2: 'text-sky-400 border-sky-500/30',
    3: 'text-emerald-400 border-emerald-500/30',
    50: 'text-purple-400 border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.4)]',
  };
  const themeColor = faseCores[state.faseAtual] || 'text-slate-200 border-slate-700';

  if (state.status === 'MENU') return null;

  const showTendaMidRun = isActiveRunStatus(state.status);

  return (
    <>
      <header className="w-full max-w-xl px-2 sm:px-3 py-1.5 sm:py-2 flex items-center justify-between shrink-0 z-20 animate-in slide-in-from-top-4 duration-300 mx-auto">
        
        {/* Lado Esquerdo: Status (Moedas, Vidas, Escudo, Tenda, Sair) */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {showTendaMidRun && (
            <button
              onMouseEnter={playHover}
              onClick={() => { playClick(); setShowConfirmQuit(true); }}
              className="glass-panel px-1.5 sm:px-2.5 py-1 rounded-full flex items-center gap-1 border border-red-500/40 hover:border-red-400 hover:bg-red-500/20 text-red-400 transition shrink-0"
              title="Desistir da partida atual e voltar à Odisseia de Fases"
            >
              <LogOut size={13} />
              <span className="font-cyber font-bold text-[10px] uppercase tracking-widest hidden sm:inline">Sair</span>
            </button>
          )}

          <div
            key={`coin-${coinPulse}`}
            className={`glass-panel px-2 sm:px-2.5 py-1 rounded-full flex items-center gap-1 border-b-2 shadow-sm ${themeColor} ${coinPulse ? 'juice-coin-pop' : ''}`}
          >
            <Coins size={13} className="filter drop-shadow-[0_0_5px_currentColor]" />
            <span className="font-cyber font-bold text-xs sm:text-sm tracking-wider">{state.moedas}</span>
          </div>

          <div
            key={`heart-${heartHit}`}
            className={`glass-panel px-2 sm:px-2.5 py-1 rounded-full flex items-center gap-1 border-b-2 border-red-500/40 ${heartHit ? 'juice-heart-hit' : ''}`}
          >
            <Heart size={13} className="text-red-500 filter drop-shadow-[0_0_5px_rgba(239,68,68,0.8)]" fill="currentColor" />
            <span className="font-cyber font-bold text-xs sm:text-sm text-red-100">{state.vidas}/{state.maxVidas}</span>
          </div>

          {state.inventario.escudo_palmares > 0 && (
            <div className="glass-panel px-1.5 sm:px-2 py-1 rounded-full flex items-center gap-1 border border-sky-500/40 shrink-0">
              <Shield size={12} className="text-sky-400 animate-pulse" />
              <span className="font-cyber font-bold text-xs text-sky-100">{state.inventario.escudo_palmares}</span>
            </div>
          )}

          {showTendaMidRun && (
            <button
              onMouseEnter={playHover}
              onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'TENDA' }); }}
              className="glass-panel px-1.5 sm:px-2 py-1 rounded-full flex items-center gap-1 border-b-2 border-amber-500/40 hover:border-amber-400 transition"
              title="Tenda de Saberes"
            >
              <ShoppingCart size={13} className="text-amber-400" />
              <span className="font-cyber font-bold text-[10px] text-amber-200 uppercase tracking-widest hidden sm:inline">Tenda</span>
            </button>
          )}
        </div>

        {/* Lado Direito: Ciclo/Fase + Botão Mudo Integrado */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <div className="glass-panel px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-xl border-r-2 flex flex-col items-end shrink-0">
            <span className="text-[8px] sm:text-[9px] text-slate-400 uppercase tracking-widest leading-none hidden sm:block">
              {state.cicloAtual}
            </span>
            <span className={`font-cyber font-bold text-xs sm:text-sm uppercase tracking-wider leading-none sm:mt-0.5 ${themeColor.split(' ')[0]}`}>
              Fase {state.faseAtual}
            </span>
          </div>

          {/* Audio Mute toggle integrado no HUD */}
          <button
            onClick={() => { playClick(); toggleMute(); }}
            className="p-1.5 sm:p-2 rounded-xl glass-panel text-slate-400 hover:text-white transition shadow-neon-cyan shrink-0 relative after:absolute after:-inset-1.5 after:content-['']"
            title="Alternar Som"
          >
            {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>
        </div>

      </header>

      {/* Modal de Confirmação para Desistir da Partida */}
      {showConfirmQuit && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="glass-panel max-w-xs sm:max-w-sm w-full p-5 rounded-2xl border-red-500/50 text-center flex flex-col items-center gap-3 shadow-[0_0_30px_rgba(239,68,68,0.25)]">
            <AlertTriangle size={36} className="text-amber-400 animate-pulse" />
            <h3 className="text-fluid-subtitle font-cyber font-bold text-white">Desistir da Partida?</h3>
            <p className="text-slate-300 text-fluid-xs leading-relaxed">
              Você voltará para a Seleção de Fases. Seu progresso salvo da jornada será mantido.
            </p>
            <div className="flex gap-2 w-full mt-2">
              <button
                onClick={() => { playClick(); setShowConfirmQuit(false); }}
                className="flex-1 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 font-cyber text-fluid-xs hover:bg-slate-700 transition"
              >
                Continuar
              </button>
              <button
                onClick={() => {
                  setShowConfirmQuit(false);
                  playBack();
                  dispatch({ type: 'NAVIGATE', payload: 'FASE_SELECT' });
                }}
                className="flex-1 py-2 rounded-xl bg-red-600 border border-red-400 text-white font-cyber text-fluid-xs font-bold hover:bg-red-500 transition shadow-neon-red"
              >
                Sair da Fase
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
